import { useState } from "react";
import type { CustomerContact, CreateContactInput } from "../types";
import { addressBookErrorMessage } from "../error-message";
import {
  useCustomerContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  useSetDefaultContact,
  useToggleFavoriteContact,
} from "../api";
import { ContactCard } from "../components/ContactCard";
import { ContactForm } from "../components/ContactForm";
import { Button, Chip, Input } from "@heroui/react";
import { Plus, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/page-chrome";
import { useTranslation } from "react-i18next";

export function AddressBook() {
  const { t } = useTranslation("app");
  const [search, setSearch] = useState("");
  const [filterFavorites, setFilterFavorites] = useState<boolean | undefined>();
  const [filterLabel, setFilterLabel] = useState<string | undefined>();
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<
    CustomerContact | undefined
  >();

  const { toast } = useToast();

  const { data, isLoading } = useCustomerContacts({
    search,
    isFavorite: filterFavorites,
    label: filterLabel,
  });

  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  const setDefaultMutation = useSetDefaultContact();
  const toggleFavoriteMutation = useToggleFavoriteContact();

  const handleSubmit = async (input: CreateContactInput) => {
    try {
      if (editingContact) {
        await updateMutation.mutateAsync({
          id: editingContact.id,
          input,
        });
        toast({
          title: t("addressBook.toast.updatedTitle"),
          description: t("addressBook.toast.updatedDescription"),
        });
      } else {
        await createMutation.mutateAsync(input);
        toast({
          title: t("addressBook.toast.addedTitle"),
          description: t("addressBook.toast.addedDescription"),
        });
      }
      setFormOpen(false);
      setEditingContact(undefined);
    } catch (error: unknown) {
      toast({
        title: t("addressBook.toast.errorTitle"),
        description: addressBookErrorMessage(
          error,
          t("addressBook.toast.saveError")
        ),
        variant: "destructive",
      });
    }
  };

  const handleEdit = (contact: CustomerContact) => {
    setEditingContact(contact);
    setFormOpen(true);
  };

  const handleDelete = async (contact: CustomerContact) => {
    if (
      !confirm(
        t("addressBook.toast.deleteConfirm", { name: contact.contactName })
      )
    )
      return;

    try {
      await deleteMutation.mutateAsync(contact.id);
      toast({
        title: t("addressBook.toast.deletedTitle"),
        description: t("addressBook.toast.deletedDescription"),
      });
    } catch (error: unknown) {
      toast({
        title: t("addressBook.toast.errorTitle"),
        description: addressBookErrorMessage(
          error,
          t("addressBook.toast.deleteError")
        ),
        variant: "destructive",
      });
    }
  };

  const handleToggleFavorite = async (contact: CustomerContact) => {
    try {
      await toggleFavoriteMutation.mutateAsync(contact.id);
    } catch (error: unknown) {
      toast({
        title: t("addressBook.toast.errorTitle"),
        description: addressBookErrorMessage(
          error,
          t("addressBook.toast.favoriteError")
        ),
        variant: "destructive",
      });
    }
  };

  const handleSetDefault = async (contact: CustomerContact) => {
    try {
      await setDefaultMutation.mutateAsync(contact.id);
      toast({
        title: t("addressBook.toast.defaultUpdatedTitle"),
        description: t("addressBook.toast.defaultUpdatedDescription", {
          name: contact.contactName,
        }),
      });
    } catch (error: unknown) {
      toast({
        title: t("addressBook.toast.errorTitle"),
        description: addressBookErrorMessage(
          error,
          t("addressBook.toast.defaultError")
        ),
        variant: "destructive",
      });
    }
  };

  const handleAddNew = () => {
    setEditingContact(undefined);
    setFormOpen(true);
  };

  type FilterKey = "all" | "favorites" | "Home" | "Office" | "Warehouse";
  const activeFilter: FilterKey = filterFavorites
    ? "favorites"
    : filterLabel === "Home" ||
        filterLabel === "Office" ||
        filterLabel === "Warehouse"
      ? filterLabel
      : "all";

  const setFilter = (f: FilterKey) => {
    if (f === "all") {
      setFilterFavorites(undefined);
      setFilterLabel(undefined);
    } else if (f === "favorites") {
      setFilterFavorites(true);
      setFilterLabel(undefined);
    } else {
      setFilterFavorites(undefined);
      setFilterLabel(f);
    }
  };

  return (
    <div className="space-y-9">
      <PageHeader
        eyebrow={t("addressBook.eyebrow")}
        title={t("addressBook.title")}
        italic={t("addressBook.italic")}
        lede={t("addressBook.lede")}
        right={
          <Button
            color="primary"
            startContent={<Plus className="w-4 h-4" />}
            onPress={handleAddNew}
          >
            {t("addressBook.addContact")}
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
        <Input
          aria-label={t("addressBook.searchLabel")}
          placeholder={t("addressBook.searchPlaceholder")}
          value={search}
          onValueChange={setSearch}
          variant="flat"
          startContent={<Search className="h-4 w-4 text-default-400" />}
          className="sm:max-w-md flex-1"
        />

        <div className="flex items-center gap-1.5 flex-wrap">
          {(
            [
              { k: "all", labelKey: "all" },
              { k: "favorites", labelKey: "favorites" },
              { k: "Office", labelKey: "office" },
              { k: "Warehouse", labelKey: "warehouse" },
              { k: "Home", labelKey: "home" },
            ] as { k: FilterKey; labelKey: string }[]
          ).map((f) => (
            <Chip
              key={f.k}
              as="button"
              size="sm"
              variant={activeFilter === f.k ? "solid" : "bordered"}
              color={activeFilter === f.k ? "primary" : "default"}
              onClick={() => setFilter(f.k)}
              className="cursor-pointer"
            >
              {t(`addressBook.filters.${f.labelKey}`)}
            </Chip>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full text-center text-default-500 py-12">
            {t("addressBook.loading")}
          </div>
        ) : !data?.contacts.length ? (
          <div className="col-span-full text-center text-default-500 py-12">
            <p className="text-lg">{t("addressBook.emptyTitle")}</p>
            <p className="text-sm mt-2">
              {search || filterFavorites || filterLabel
                ? t("addressBook.emptyFiltered")
                : t("addressBook.emptyDefault")}
            </p>
          </div>
        ) : (
          data.contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleFavorite={handleToggleFavorite}
              onSetDefault={handleSetDefault}
            />
          ))
        )}
      </div>

      <ContactForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingContact(undefined);
        }}
        onSubmit={handleSubmit}
        initialData={editingContact}
        isLoading={updateMutation.isPending || createMutation.isPending}
      />
    </div>
  );
}
